/**
 * Chrome slots (RFC 0284 S3).
 *
 * A slot is a named surface outside the content tree: the sidebar, the canvas
 * side column, the header. RFC 0266 defined chrome by a rule, never two and
 * never split, and that rule is what makes a slot different from a leaf. A leaf
 * repeats and nests; a slot is filled once.
 *
 * WHAT THIS IS FOR. `WorldShell` imports `SidebarPanel` directly, so the column
 * cannot be turned off, replaced, or written by anybody else. This is the
 * indirection that ends that, and the whole of S3 is making the SHIPPED sidebar
 * go through it. A registry whose own built-ins bypass it is two mechanisms with
 * one of them untested, which is the state RFC 0273's `panels` is in today and
 * the reason this exists.
 *
 * WHY A REGISTRY AND NOT A PROP. The shell would otherwise have to know every
 * fill in order to pass it down, which is the same coupling by a longer
 * route. A registry is also what lets the installed tier (S5) appear beside a
 * built-in without the shell learning anything new.
 *
 * NOT YET INSTALLED APPS. This resolves BUILT-INS only. An installed
 * fill needs `mod.window` (S2) to do anything at all, since a sidebar
 * that cannot open a document is a list of names. The seam is here and the
 * second source is S4/S5's.
 */

import type { SettingSpec, SettingValue, Slot, SlotFill } from '@mod/core/model'
import type { ReactNode } from 'react'
import type { ComposerSlotProps } from './composer/composerSlot'
import type { HeaderSlotProps } from './header/headerSlot'
import type { SessionHeaderSlotProps } from './session-header/sessionHeaderSlot'
import type { SidebarSlotProps } from './sidebar/sidebarSlot'

export type { Slot, SlotFill }

/**
 * What each slot hands its fill.
 *
 * Per slot, because the surfaces genuinely differ: a sidebar is given the
 * working set and the commands that act on it, while a panel section is given
 * the document the canvas is showing. One shared props type would be the union
 * of every surface's needs, and every fill would receive fields that
 * mean nothing to it.
 *
 * `panel` is `unknown` until RFC 0273's sections move onto this registry.
 * Naming it now is not speculation: it is the reason `Slot` is a closed set, and
 * leaving it out would make the map look like the slot list when it is not.
 */
export interface SlotPropsMap {
  sidebar: SidebarSlotProps
  panel: unknown
  header: HeaderSlotProps
  /** S8d. Given the space's sessions, which one it is pointed at, what is
   *  already on screen and one way to send; it decides for itself whether to
   *  draw at all. */
  composer: ComposerSlotProps
  /** The strip above one conversation. Given that session and the transcript
   *  the pane already holds, so a widget about it costs no second read. */
  sessionHeader: SessionHeaderSlotProps
}

/**
 * A fill whose module is bundled with the shell.
 *
 * `render` rather than `mount`: this tier is React and returns a node, so the
 * shell composes it the way it composes anything else. An installed
 * fill mounts into a container instead (RFC 0275), and the difference
 * is real enough that pretending they share one signature would cost a wrapper
 * around every built-in for the benefit of code that does not exist yet.
 *
 * NO `kind`, which is the one field of the catalog shape a built-in cannot
 * honestly fill. `kind` says how a VIEW DOCUMENT is turned into content, and a
 * bundled component has no view document: it is neither mounted in an iframe
 * nor projected from rows. Declaring `kind: 'view'` here to satisfy the type
 * would be a lie the first installed fill has to work around.
 */
export interface BuiltinFill<S extends Slot = Slot>
  extends Omit<SlotFill, 'kind' | 'slot'> {
  slot: S
  builtin: true
  render: (props: SlotPropsMap[S]) => ReactNode
}

/**
 * How a fill is STORED, with its per-slot props type erased.
 *
 * `never` as the parameter, which is the one type every renderer's props is
 * assignable to (function parameters are contravariant), so a sidebar renderer
 * goes in without a cast. Coming back out needs one, because the map cannot
 * prove the key and the value agree. The key IS the proof: `registerBuiltin`
 * files under `fill.slot` and nothing else writes to the map.
 */
type StoredBuiltin = Omit<SlotFill, 'kind' | 'slot'> & {
  slot: Slot
  builtin: true
  render: (props: never) => ReactNode
}

/**
 * The built-in registry.
 *
 * Module-level and populated at import time, which is what keeps cold start
 * unchanged: a bundled fill needs no document, so the first frame draws
 * the same chrome it always did. An installed one cannot manage that and must
 * wait for the catalog, which is why S4 resolves built-ins FIRST.
 */
const builtins = new Map<Slot, StoredBuiltin[]>()

/**
 * Register a built-in. Idempotent by (slot, id), so a hot reload re-registering
 * a module replaces its fill instead of showing it twice.
 */
export function registerBuiltin<S extends Slot>(fill: BuiltinFill<S>): void {
  const list = builtins.get(fill.slot) ?? []
  const at = list.findIndex((c) => c.id === fill.id)
  if (at >= 0) list[at] = fill
  else list.push(fill)
  builtins.set(fill.slot, list)
}

/**
 * What fills a slot, in render order.
 *
 * Returns an ARRAY, and an empty one is a supported answer: a slot with nothing
 * in it renders nothing, not a placeholder. That is the invariant the whole
 * proposal turns on, because "I do not want a sidebar" has to be a state the
 * shell can be in rather than a state it treats as broken.
 */
export function builtinFillsFor<S extends Slot>(slot: S): BuiltinFill<S>[] {
  // The cast the store's `never` costs. Sound because the key is the slot: a
  // fill filed under 'sidebar' was declared `slot: 'sidebar'`, so its
  // renderer takes sidebar props.
  return (builtins.get(slot) ?? []) as unknown as BuiltinFill<S>[]
}

/** One fill by id, or undefined. What an arrangement (S4) resolves
 *  against once a person can choose which sidebar they want. */
export function builtinById<S extends Slot>(
  slot: S,
  id: string,
): BuiltinFill<S> | undefined {
  return builtinFillsFor(slot).find((c) => c.id === id)
}

/**
 * What actually fills a slot, given what the layout says (RFC 0284 S4b).
 *
 * `fill` is `slotFill(layout, slot)`, and its three states are three different
 * answers rather than two:
 *
 *   undefined  the layout is SILENT. Fall back to the built-ins, which is every
 *              World written before layouts and every space nobody arranged.
 *   []         the person EMPTIED the slot. Render nothing. This is the state
 *              "I do not want a sidebar" has to be expressible as, and the whole
 *              reason `slotFill` distinguishes the two.
 *   ids        the layout NAMES fills, in render order.
 *
 * AN ID THAT RESOLVES TO NOTHING IS DROPPED, not substituted. Today that is
 * every installed fill, because only built-ins can mount until S5. The
 * alternative, silently falling back to the built-in sidebar, would tell a
 * person their chosen sidebar is installed and working when it is neither.
 *
 * The cost is real and is the RFC's open question: a layout naming only apps
 * that cannot mount yet renders an empty slot. That is honest, and it is why
 * emptying a slot has to be reachable from somewhere other than the sidebar.
 */
export function resolveSlot<S extends Slot>(
  slot: S,
  fill: readonly string[] | undefined,
): BuiltinFill<S>[] {
  if (fill === undefined) return builtinFillsFor(slot)
  return fill
    .map((id) => builtinById(slot, id))
    .filter((c): c is BuiltinFill<S> => c !== undefined)
}

/**
 * What fills a slot, built-ins AND installed apps (RFC 0284 S6a).
 *
 * Supersedes `resolveSlot`, which could only answer with what the shell
 * bundles. Same three states, one more kind of answer: an id now resolves to a
 * built-in, or to an installed fill the shell will mount, or to nothing
 * at all.
 *
 * THE ORDER OF THE LOOKUP IS THE POLICY. Built-in first, so a person who names
 * `mod.sidebar` gets the shipped one instantly rather than after the catalog
 * arrives. Installed second, and only for an app whose realm is `host`: a
 * sandboxed fill cannot hold a live handle, and chrome that cannot
 * follow the working set is chrome that is always slightly wrong.
 *
 * STILL DROPS WHAT IT CANNOT RESOLVE, for the reason it always did. An id
 * naming nothing is not silently replaced by the built-in, because that tells a
 * person their chosen sidebar is working when it is not there.
 */
export function fillSlot<S extends Slot>(
  slot: S,
  fill: readonly string[] | undefined,
  installed: readonly SlotApp[],
): ResolvedSlot<S>[] {
  if (fill === undefined)
    return builtinFillsFor(slot).map((c) => ({ kind: 'builtin', builtin: c }))
  const out: ResolvedSlot<S>[] = []
  for (const id of fill) {
    const builtin = builtinById(slot, id)
    if (builtin) {
      out.push({ kind: 'builtin', builtin })
      continue
    }
    // BY CONTRIBUTION ID, not by app id. A built-in is named `mod.sidebar`, so
    // an installed one has to be named the same way or a layout would mean two
    // different things depending on what it happened to name. It also lets one
    // app contribute to more than one slot, which `SlotFill.id` exists
    // for ("unique within the app").
    const app = installed.find(
      (a) =>
        (a.realm === 'host' ||
          slot === 'sidebar' ||
          slot === 'sessionHeader') &&
        (a.fills ?? []).some(
          (c) => c.id === id && c.slot === slot && c.kind !== 'items',
        ),
    )
    if (app) out.push({ kind: 'installed', app })
  }
  return out
}

/**
 * What fills a LIST slot: everything that declares it, unless a layout says
 * otherwise (RFC 0284 §4a, the many-fill case).
 *
 * `fillSlot` above answers for a slot that is FILLED ONCE — a window has one
 * sidebar, one header, one composer — so a silent layout there means "the
 * built-in", and an installed fill appears only when somebody names it.
 * A list slot is the opposite: several widgets sit side by side, and installing
 * an app that adds one is the whole gesture. Waiting for a layout to name it
 * would mean installing an app and seeing nothing, then editing an arrangement
 * to find out what you just installed.
 *
 * The three states are still three, and the middle one is why this cannot be a
 * truthiness check:
 *
 *   undefined  the layout is SILENT. Everything that declares the slot, in
 *              built-in-then-installed order.
 *   []         the person EMPTIED the slot. Nothing draws.
 *   ids        the layout NAMES fills, in that order — `fillSlot`'s
 *              answer exactly, including its refusal to substitute for an id
 *              that resolves to nothing.
 */
export function fillSlotAll<S extends Slot>(
  slot: S,
  fill: readonly string[] | undefined,
  installed: readonly SlotApp[],
): ResolvedSlot<S>[] {
  if (fill !== undefined) return fillSlot(slot, fill, installed)
  const out: ResolvedSlot<S>[] = builtinFillsFor(slot).map((c) => ({
    kind: 'builtin',
    builtin: c,
  }))
  for (const app of installed) {
    // Sandboxed views can fill the sidebar and session header through the
    // JSON bridge. Other chrome slots still require host-realm modules.
    if (app.realm !== 'host' && slot !== 'sidebar' && slot !== 'sessionHeader')
      continue
    if (!(app.fills ?? []).some((c) => c.slot === slot && c.kind !== 'items'))
      continue
    out.push({ kind: 'installed', app })
  }
  return out
}

/**
 * An installed app, as slot resolution needs to see it.
 *
 * A subset of `AppSummary` rather than the thing itself, so this module stays
 * free of the catalog: slots are about what draws a surface, not about where
 * the record came from.
 */
export interface SlotApp {
  id: string
  name: string
  realm?: 'host'
  viewDocId?: string
  /** What this app fills, and under what ids. `AppRef.panels` on the catalog,
   *  which RFC 0284 S3 generalised from panel sections to any slot. */
  fills?: readonly { id: string; slot?: Slot; kind?: 'view' | 'items' }[]
  /** What it can be configured to do, and what it is set to (S6g). Carried so
   *  the mount can build the capability without a second lookup. */
  settings?: SettingSpec[]
  settingValues?: Record<string, SettingValue>
}

/** What ends up in a slot: something bundled, or something installed. */
export type ResolvedSlot<S extends Slot = Slot> =
  | { kind: 'builtin'; builtin: BuiltinFill<S> }
  | { kind: 'installed'; app: SlotApp }

/** Test seam. The registry is module state, so a test that registers a
 *  fill would otherwise leak it into the next one. */
export function resetBuiltins(): void {
  builtins.clear()
}
